import type { SupportDay, SupportHours, SupportMessage, SupportRequest } from '../api/types';

/**
 * The pure half of the Support screen: merging a polled conversation, turning
 * the desk's week into an editable form and back, and naming how a request
 * ended. Kept out of the components so it can be tested without a browser.
 */

export const DAYS: { key: SupportDay; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const STORED_ID = /^\d+$/;
const compareIds = (a: string, b: string) => a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);

/**
 * A conversation as the poll and a send both deliver it: each id once, in id
 * order — which sphere-crm guarantees is commit order per thread, so this is
 * the order things were said in.
 */
export function mergeSupportMessages(current: SupportMessage[], incoming: SupportMessage[]): SupportMessage[] {
  const byId = new Map<string, SupportMessage>();
  for (const message of [...current, ...incoming]) byId.set(message.id, message);
  return [...byId.values()].filter((m) => STORED_ID.test(m.id)).sort((a, b) => compareIds(a.id, b.id));
}

/** Where the tail poll picks up. */
export function lastMessageId(messages: SupportMessage[]): string {
  let last = '0';
  for (const message of messages) {
    if (STORED_ID.test(message.id) && compareIds(message.id, last) > 0) last = message.id;
  }
  return last;
}

/** One row of the hours form. */
export interface DayDraft {
  open: boolean;
  from: string;
  to: string;
}

export interface HoursDraft {
  timezone: string;
  days: Record<SupportDay, DayDraft>;
}

const DEFAULT_FROM = '09:00';
const DEFAULT_TO = '17:00';

/**
 * The form for a stored week — or, with none stored, a sensible start: weekdays
 * nine to five in the browser's own zone, which is the zone the person setting
 * it is most likely working in.
 */
export function draftFromHours(hours: SupportHours | null, fallbackZone: string): HoursDraft {
  const days = {} as Record<SupportDay, DayDraft>;
  for (const { key } of DAYS) {
    const slot = hours ? hours.days[key] : ['sat', 'sun'].includes(key) ? null : { open: DEFAULT_FROM, close: DEFAULT_TO };
    days[key] = slot
      ? // The CRM accepts 24:00 as "until midnight"; a time input cannot show it.
        { open: true, from: slot.open, to: slot.close === '24:00' ? '23:59' : slot.close }
      : { open: false, from: DEFAULT_FROM, to: DEFAULT_TO };
  }
  return { timezone: hours?.timezone || fallbackZone, days };
}

/**
 * The week the form describes, as the CRM stores it — or the first thing wrong
 * with it, said the way the CRM would. The CRM validates again; this is so the
 * person editing finds out before pressing Save.
 */
export function hoursFromDraft(draft: HoursDraft): { hours: SupportHours } | { error: string } {
  const timezone = draft.timezone.trim();
  if (!timezone) return { error: 'Pick the time zone the support desk works in.' };
  const days = {} as SupportHours['days'];
  for (const { key, label } of DAYS) {
    const day = draft.days[key];
    if (!day.open) {
      days[key] = null;
      continue;
    }
    if (!/^\d{2}:\d{2}$/.test(day.from) || !/^\d{2}:\d{2}$/.test(day.to)) {
      return { error: `${label}: both times are needed.` };
    }
    if (day.to <= day.from) {
      return { error: `${label}: closes before it opens. Overnight hours need a row for each day.` };
    }
    days[key] = { open: day.from, close: day.to };
  }
  return { hours: { timezone, days } };
}

/** How a request ended, in the words a Recent list wants. */
export function outcome(request: Pick<SupportRequest, 'status' | 'closedReason' | 'closedBy'>): string {
  switch (request.closedReason) {
    case 'staff':
      return 'Answered and closed';
    case 'operator':
      return 'Operator ended it';
    case 'unanswered':
      return 'Nobody picked it up';
    case 'idle':
      return 'Went quiet';
    default:
      return request.status;
  }
}

/** Every IANA zone this browser knows, for the hours form's suggestions. */
export function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  } catch {
    return [];
  }
}
